export interface CaseStudyPageData {
  slug: string
  company: string
  role: string
  timeframe: string
  heroMetric: string
  heroMetricLabel: string
  problem: string
  whatIBuilt: { heading: string; content: string }[]
  results: { value: string; label: string }[]
  lesson: string
}

export const caseStudyPages: CaseStudyPageData[] = [
  {
    slug: 'genentech',
    company: 'Genentech (Roche)',
    role: 'Principal Product Manager, Decision Intelligence',
    timeframe: '2025 to Present',
    heroMetric: '$96M',
    heroMetricLabel: 'attributable impact',
    problem:
      "The company allocates over $1.2B annually in marketing spend across multiple therapeutic brands and business units. When I joined, the measurement function was built for an earlier stage of the business. As the portfolio grew, the existing models and data pipelines hadn't yet been unified into a single framework for translating outputs into allocation decisions. The organization had strong analytics. The opportunity was turning those analytics into a repeatable decision framework.\n\nThe deeper opportunity was organizational. The company had grown fast, and the measurement function hadn't yet scaled to match. Teams were optimizing locally, which is natural at that stage of maturity. The opportunity was building the connective tissue between analytics, activation, and brand strategy so that model outputs could flow into allocation decisions at the speed the business required.",
    whatIBuilt: [
      {
        heading: 'The Platform',
        content:
          'I designed and own the enterprise Bayesian Marketing Mix Modeling platform that serves as the single source of truth for marketing allocation decisions across the organization. The platform replaced earlier frequentist models with a modern Bayesian engine capable of producing posterior distributions, not point estimates, for every channel and brand. This matters because allocation decisions at this scale require uncertainty quantification. A point estimate of $4.2M ROI is useless without knowing the credible interval. Bayesian posteriors give decision-makers the ability to reason about risk, not just expected value. I shipped an agentic data science workflow that automated prior selection and iterative modeling. What previously took weeks of manual analyst configuration now runs in a fraction of the time, achieving 5x faster time-to-insight across brands.',
      },
      {
        heading: 'The Org',
        content:
          'I built and now lead a ~40-person cross-functional organization spanning Data Science, ML Engineering, Data Engineering, Activation, and Digital Insights. The org design was deliberate: I wanted every function working against shared decision outcomes rather than optimizing independently. This meant creating shared governance rituals (quarterly model release cycles with T+n notation), a Pre-Model Intake Gate that forces data quality accountability before modeling begins, and a Data Drop Tracker that provides early visibility into data issues before they compound downstream.',
      },
      {
        heading: 'The Governance Layer',
        content:
          'I built a full quarterly model release framework: a 90-day cycle covering data finalization, modeling, product check, and SQA, with sub-phase breakdowns responding to activation team feedback. This framework became the mechanism through which brand teams, agency partners, and internal stakeholders align on what the models say and what to do about it.',
      },
    ],
    results: [
      {
        value: '$96M',
        label: 'in attributable business impact with 8% ROAS improvement',
      },
      { value: '$1.2B+', label: 'in annual marketing allocation governed' },
      {
        value: '5x',
        label: 'faster time-to-insight through agentic workflow automation',
      },
      { value: '~40', label: 'person cross-functional org built from scratch' },
    ],
    lesson:
      "Measurement platforms don't fail because the math is wrong. They fail because nobody designed the decision layer: who sees the output, when they see it, what authority they have to act on it, and what happens when the model disagrees with their intuition. The platform works because I built the governance infrastructure alongside the technical infrastructure. Neither works without the other.",
  },
  {
    slug: 'walmart',
    company: 'Walmart',
    role: 'Product Manager, Marketplace Intelligence',
    timeframe: '2023 to 2025',
    heroMetric: '$4.5B',
    heroMetricLabel: 'in GMV decisions governed',
    problem:
      'Walmart Marketplace was scaling aggressively to compete in the third-party seller ecosystem. The growth was working. Sellers were joining. The next challenge was building the decision infrastructure to govern quality, risk, and growth dynamics at the pace the marketplace demanded. As the marketplace scaled, seller approval, pricing, and risk decisions were distributed across multiple product lines, each with its own signals and logic. The opportunity was building a unified system that could balance growth incentives against quality and risk at the portfolio level.',
    whatIBuilt: [
      {
        heading: 'Real-Time ML Inference',
        content:
          'I stood up real-time ML inference serving production traffic at 99.9% uptime. This was the production backbone for seller risk scoring, approval routing, and pricing optimization. The system had to make decisions at transaction speed while maintaining auditability for compliance review.',
      },
      {
        heading: 'RAG Workflows',
        content:
          'I deployed production RAG (Retrieval-Augmented Generation) workflows that cut operational costs by $1.8M/year. These workflows automated seller communication, policy interpretation, and case resolution processes that previously required manual review queues with multi-day turnaround.',
      },
      {
        heading: 'Cross-Functional Prioritization',
        content:
          'As the platform matured, the coordination challenge grew with it. Engineering, data science, and business operations each brought their own priorities and success metrics, which is expected in a fast-scaling organization. I managed cross-functional prioritization to ensure the ML systems we built aligned with the business outcomes the marketplace cared about: seller retention, buyer trust, and GMV growth balanced against acceptable risk thresholds.',
      },
    ],
    results: [
      { value: '$4.5B', label: 'in GMV decisions governed' },
      { value: '99.9%', label: 'uptime on real-time ML inference' },
      { value: '$1.8M/yr', label: 'in operational savings' },
      {
        value: 'Cross-functional',
        label: 'alignment across engineering, DS, and operations',
      },
    ],
    lesson:
      "Marketplace intelligence isn't a data problem. It's a prioritization problem. Every signal you add to a seller risk model creates a constituency that wants to interpret it differently. The PM's job isn't to build the model. It's to build the decision framework that holds up when multiple teams are pulling in different directions.",
  },
  {
    slug: 'amazon',
    company: 'Amazon',
    role: 'Senior Product Manager, Recommendations',
    timeframe: '2021 to 2023',
    heroMetric: '~$450M',
    heroMetricLabel: 'in Year-1 savings',
    problem:
      "Amazon's recommendation systems serve roughly 1 billion inferences daily across retail. When I took ownership, the ranking infrastructure had been built through years of incremental optimization across multiple teams. Each surface had strong local performance. The opportunity was creating a coherent multi-year technical vision that spanned surfaces and unified the transition to modern ranking architectures. The vendor ecosystem around these systems had also evolved. AI-driven services powering recommendations were operating under contracts that hadn't been revisited to reflect current capabilities and market conditions.",
    whatIBuilt: [
      {
        heading: 'Vision and Roadmap',
        content:
          'I owned the vision and multi-year roadmap for the recommendation systems portfolio. This meant making deliberate calls about where to invest and where to consolidate. I drove the ranking model evolution from GBDT (Gradient Boosted Decision Trees) to Transformer-based architectures to embedding-based models. Each transition required alignment across science, engineering, and business teams who had different timelines and different definitions of readiness.',
      },
      {
        heading: 'Vendor Negotiation',
        content:
          'I unlocked ~$450M in Year-1 savings through AI-driven vendor negotiation. This required building a quantitative framework that mapped vendor service quality to business outcomes, then using that framework to renegotiate contracts from a position of data-backed leverage.',
      },
      {
        heading: 'Experimentation Framework',
        content:
          'I aligned product, science, and engineering leadership around a unified experimentation and release framework. This created a shared methodology for running and comparing experiments across surfaces, making it possible to compound learnings across the portfolio rather than running isolated tests.',
      },
    ],
    results: [
      { value: '~1B', label: 'daily inferences served' },
      { value: '~$450M', label: 'in Year-1 savings' },
      {
        value: 'GBDT → Transformer → Embedding',
        label: 'ranking evolution driven',
      },
      { value: 'Unified', label: 'experimentation framework across surfaces' },
    ],
    lesson:
      "At billion-inference scale, the bottleneck is never compute. It's alignment. Every architectural transition required convincing dozens of stakeholders that short-term regression risk was worth long-term platform leverage. The PM's job at this scale is to hold the long-term vision while giving teams enough local autonomy to execute without waiting for top-down permission.",
  },
  {
    slug: 'enfusion',
    company: 'Enfusion',
    role: 'Product Manager, AI Risk Platforms',
    timeframe: '2018 to 2021',
    heroMetric: '$200B+',
    heroMetricLabel: 'AUM served',
    problem:
      "Hedge funds managing hundreds of billions in assets operate under extreme regulatory and risk constraints. A single compliance gap or undetected risk exposure can trigger significant consequences: blocked trades, regulatory action, investor concern. The risk and compliance tooling available to institutional investors at the time was either built bespoke by each fund internally (expensive and hard to maintain) or available off the shelf (standardized but not flexible enough for multi-strategy portfolios). The market was ready for a platform approach: standardized enough to scale across 50+ funds, flexible enough to handle the idiosyncrasies of each fund's strategy and regulatory environment.",
    whatIBuilt: [
      {
        heading: 'Product and Investment Strategy',
        content:
          "I defined the product and investment strategy for Enfusion's AI-powered risk and compliance platforms. This meant deciding which risk models to build in-house versus integrate from third parties, how to structure the platform's extensibility for different fund strategies, and how to price and package the product for a market that was accustomed to either building internally or accepting more generic tools.",
      },
      {
        heading: 'Explainability-First ML Standards',
        content:
          "I established explainability-first ML standards that became the org's default governance model. In institutional finance, a risk model needs to be transparent. Fund managers and CROs need to understand why a trade was flagged, why a risk limit was triggered, and what the model's assumptions are. I built these requirements into the product from day one as a core design principle.",
      },
      {
        heading: 'Direct Stakeholder Partnership',
        content:
          'I partnered directly with fund managers and Chief Risk Officers to translate regulatory constraints into product requirements. This meant sitting across the table from people managing billions and understanding the space between what their regulators required, what their current tools provided, and what they actually needed to make better risk decisions.',
      },
    ],
    results: [
      { value: '$200B+', label: 'in AUM served across 50+ hedge funds' },
      { value: '$400M', label: 'in blocked trades prevented' },
      {
        value: 'Explainability-first',
        label: 'ML standards as org-wide default',
      },
      {
        value: 'Direct CRO',
        label: 'and fund manager partnerships shaping strategy',
      },
    ],
    lesson:
      "Risk platforms are trust products. The math has to be right, obviously. But adoption depends on whether the people making nine-figure decisions believe the system understands their constraints. That belief doesn't come from model accuracy metrics. It comes from explainability, from governance, and from a product team that speaks the language of the people it serves.",
  },
]
